/**
 * What one build stage changed vs the previous one (write→ground, ground→seo, …).
 *
 * Because each stage is its own document snapshot, this is generalized: give it
 * a from/to stage and it renders the prose delta (unified diff of each side's
 * markdown) plus the STRUCTURED citation delta — citations added, removed, or
 * with a flipped verdict — which a text diff of stripped prose can't show.
 *
 * Collapsed by default: a stage that changed nothing shouldn't frame the panel.
 */
import { useEffect, useMemo, useState } from 'react';
import { Diff, Hunk, parseDiff } from 'react-diff-view';
import 'react-diff-view/style/index.css';
import api from '../services/api';
import { createGitDiff } from '../utils/contentDiff';
import type { StageCitation, StageDiffResponse } from '../types';

const STAGE_LABEL: Record<string, string> = {
  outline: 'Outline',
  write: 'Write',
  ground: 'Ground',
  seo: 'SEO',
};

function CitationLine({ c, kind }: { c: StageCitation; kind: 'added' | 'removed' | 'verdict' }) {
  const tone =
    kind === 'added'
      ? 'text-green-700 dark:text-green-300'
      : kind === 'removed'
        ? 'text-red-700 dark:text-red-300 line-through decoration-red-400'
        : 'text-amber-700 dark:text-amber-300';
  return (
    <div className="py-0.5 text-xs">
      <span className={`${tone} break-words`}>{c.text}</span>
      {kind === 'verdict' && (
        <span className="ml-1 text-gray-500 dark:text-gray-400">
          {c.from_verdict || '-'} → {c.to_verdict || '-'}
        </span>
      )}
    </div>
  );
}

export default function StageDiff({
  pageId,
  fromStage,
  toStage,
}: {
  pageId: number;
  fromStage: string;
  toStage: string;
}) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<StageDiffResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || data || loading) return;
    let live = true;
    setLoading(true);
    setError(null);
    api
      .getStageDiff(pageId, fromStage, toStage)
      .then((res) => live && setData(res))
      .catch((e) => live && setError(e instanceof Error ? e.message : 'Failed to load diff'))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pageId, fromStage, toStage]);

  const files = useMemo(() => {
    if (!data) return [];
    if (data.from_markdown === data.to_markdown) return [];
    try {
      const text = createGitDiff(data.from_markdown, data.to_markdown);
      if (!text) return [];
      return parseDiff(text).filter((f) => f.hunks && f.hunks.length > 0);
    } catch {
      return [];
    }
  }, [data]);

  const cd = data?.citation_delta;
  const hasCitationChanges =
    !!cd && (cd.added.length > 0 || cd.removed.length > 0 || cd.verdict_changed.length > 0);

  return (
    <div className="rounded border border-gray-200 dark:border-gray-700">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-2 text-xs font-medium text-indigo-700 hover:underline dark:text-indigo-300"
      >
        {open
          ? 'Hide changes'
          : `See what ${STAGE_LABEL[toStage] || toStage} changed vs ${STAGE_LABEL[fromStage] || fromStage}`}
      </button>
      {open && (
        <div className="border-t border-gray-200 px-2 py-2 text-xs dark:border-gray-700">
          {loading && <div className="text-gray-500 dark:text-gray-400">Loading diff…</div>}
          {error && <div className="text-rose-600 dark:text-rose-400">{error}</div>}
          {!loading && !error && data && !data.from_present && (
            <div className="italic text-gray-500 dark:text-gray-400">
              {STAGE_LABEL[fromStage] || fromStage} hasn't run for this version yet.
            </div>
          )}
          {!loading && !error && data?.from_present && (
            <>
              {hasCitationChanges && (
                <div className="mb-2 rounded bg-gray-50 p-2 dark:bg-gray-800/60">
                  <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    Citations
                  </div>
                  {cd!.added.map((c, i) => (
                    <CitationLine key={`a${i}`} c={c} kind="added" />
                  ))}
                  {cd!.removed.map((c, i) => (
                    <CitationLine key={`r${i}`} c={c} kind="removed" />
                  ))}
                  {cd!.verdict_changed.map((c, i) => (
                    <CitationLine key={`v${i}`} c={c} kind="verdict" />
                  ))}
                </div>
              )}
              <div className="overflow-x-auto">
                {files.length === 0 ? (
                  <div className="italic text-gray-500 dark:text-gray-400">
                    {hasCitationChanges
                      ? 'No prose changes (citation/metadata only).'
                      : 'No changes at this stage.'}
                  </div>
                ) : (
                  files.map((file, i) => (
                    <Diff
                      key={i}
                      viewType="unified"
                      diffType={file.type}
                      hunks={file.hunks || []}
                      gutterType="none"
                    >
                      {(hunks) => hunks.map((hunk) => <Hunk key={hunk.content} hunk={hunk} />)}
                    </Diff>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
